// What a measurement run cost, as the backend reports it
// (app/services/token_accounting.run_cost). Screens show this, never the flat
// per-call projection: prod run 47 projected $33 while the Claude lane alone
// measured about $101.
import type { RunCost } from "@/api/run-cost";
import { getModelLabel } from "@/app/constants/models";

function money(v: number): string {
  return `$${v.toFixed(2)}`;
}

/** One line for a run row or stat: "$101.48 measured (OpenAI, Gemini unpriced)". */
export function fmtRunCost(cost: RunCost | null | undefined): string {
  if (!cost || cost.basis === "unavailable") return "measured cost unavailable";
  if (cost.basis === "estimate") {
    return cost.estimate_usd == null ? "estimate unavailable" : `est. ${money(cost.estimate_usd)}`;
  }
  const parts = [cost.measured_usd == null ? "nothing priced" : `${money(cost.measured_usd)} measured`];
  const notes: string[] = [];
  if (cost.unpriced_lanes.length) {
    notes.push(`${cost.unpriced_lanes.map((lane) => getModelLabel(lane)).join(", ")} unpriced`);
  }
  if (cost.unmeasured_attempts > 0) {
    notes.push(`${cost.unmeasured_attempts} timed-out call${cost.unmeasured_attempts === 1 ? "" : "s"} unmeasured`);
  }
  if (notes.length) parts.push(`(${notes.join("; ")})`);
  return parts.join(" ");
}

/** Per-lane breakdown for a tooltip. */
export function runCostDetail(cost: RunCost | null | undefined): string | undefined {
  if (!cost || cost.basis !== "measured") {
    return cost?.basis === "estimate"
      ? "Flat per-call projection. The run has not started."
      : "This run recorded no token usage, so its real cost is not known.";
  }
  return Object.entries(cost.per_lane)
    .map(([lane, entry]) => {
      const label = getModelLabel(lane);
      if (entry.measured_usd == null) return `${label}: unpriced (${entry.calls} calls)`;
      const searches = entry.web_search_requests && entry.web_search_cost_usd != null
        ? `, incl. ${entry.web_search_requests} searches ${money(entry.web_search_cost_usd)}`
        : "";
      const unmeasured = entry.unmeasured_attempts ? `, ${entry.unmeasured_attempts} unmeasured` : "";
      return `${label}: ${money(entry.measured_usd)} (${entry.calls} calls${searches}${unmeasured})`;
    })
    .join("\n");
}
